import {
  trackTmApplied,
  trackTmQaFailed,
  trackTmQaRetry,
  trackTranslationCompleted,
  trackTranslationFailed,
  trackTranslationStarted,
} from '@src/analytics/EventTracker';
import { isAbortError } from '@src/utility/error';

import type { TranslationMemoryReport } from '@src/types.ts/translation';

export interface TranslationTrackingContext {
  storyId?: string;
  sourceLang: string;
  targetLang: string;
  /** Client-resolved handler id (default handler's internalName). */
  handlerId?: string;
}

export interface TranslationTrackingResult {
  /**
   * Server-confirmed handler id read after the op resolves. Falls back to
   * `ctx.handlerId` when undefined so Completed never ships a null handler.
   */
  handlerId?: string;
  phraseCount?: number;
  /**
   * Distinct translation-memory entries honored in this run (PLAT-10853) —
   * `tmEntryIds` is honored-only (E7), so this counts what the backend QA layer
   * confirmed, not what merely matched. Zero / absent ⇒ no
   * `translation_memory_applied` event.
   */
  tmEntryCount?: number;
  /** The workspace the run's glossary came from (PLAT-10875). */
  showcaseId?: string;
  /**
   * Run-level translation-memory QA outcome from the translate response
   * (PLAT-10924) — the retry / miss verdicts, which (unlike `tmEntryCount`)
   * cannot be derived from the returned phrases because `tmEntryIds` is
   * honored-only. Absent when the run applied no glossary entry.
   */
  tmReport?: TranslationMemoryReport;
}

/**
 * Wraps a translation Generate or Regenerate with Started → Completed (on
 * success) or Started → Failed (on non-abort error) tracker calls.
 *
 * Abort handling has two sources: (a) the store swallows `AbortError`
 * internally and resolves silently — `isAborted` lets the caller signal
 * that resolution was really an abort; (b) if the op does throw, we
 * re-throw `AbortError` without firing Failed.
 *
 * `readResult` runs after `run` resolves to pull `handler_id` +
 * `phrase_count` from whatever source the caller wants (typically the
 * translation cache). Errors re-throw after tracker fires so callers
 * keep their error-UI path.
 */
export async function withTranslationTracking(
  action: 'generate' | 'regenerate',
  ctx: TranslationTrackingContext,
  run: () => Promise<void>,
  readResult: () => TranslationTrackingResult = () => ({}),
  isAborted: () => boolean = () => false,
): Promise<void> {
  const t0 = performance.now();
  trackTranslationStarted({
    story_id: ctx.storyId,
    source_lang: ctx.sourceLang,
    target_lang: ctx.targetLang,
    handler_id: ctx.handlerId,
    action,
  });
  try {
    await run();
  } catch (err) {
    if (isAbortError(err)) throw err;
    trackTranslationFailed({
      story_id: ctx.storyId,
      source_lang: ctx.sourceLang,
      target_lang: ctx.targetLang,
      handler_id: ctx.handlerId,
      action,
      error_message: err instanceof Error ? err.message : undefined,
    });
    throw err;
  }
  if (isAborted()) return;
  const result = readResult();
  trackTranslationCompleted({
    story_id: ctx.storyId,
    source_lang: ctx.sourceLang,
    target_lang: ctx.targetLang,
    handler_id: result.handlerId ?? ctx.handlerId,
    action,
    duration_ms: Math.round(performance.now() - t0),
    phrase_count: result.phraseCount,
  });
  // Only when the run actually honored glossary terms (PLAT-10853) — a
  // workspace with no translation memory would otherwise emit a zero-valued
  // event on every translation and drown the signal.
  if (result.tmEntryCount) {
    trackTmApplied({
      story_id: ctx.storyId,
      showcase_id: result.showcaseId,
      source_lang: ctx.sourceLang,
      target_lang: ctx.targetLang,
      entry_count: result.tmEntryCount,
      phrase_count: result.phraseCount,
      action,
    });
  }
  // QA quality (PLAT-10924). Independent guards, so a run that retried AND still
  // missed a term fires both — that pair IS the signal ("the retry didn't save
  // it"). Each skips at zero for the same reason `_applied` does.
  const report = result.tmReport;
  if (report?.retried.length) {
    trackTmQaRetry({
      story_id: ctx.storyId,
      showcase_id: result.showcaseId,
      source_lang: ctx.sourceLang,
      target_lang: ctx.targetLang,
      retried_count: report.retried.length,
      rescued_count: report.rescued.length,
      action,
    });
  }
  if (report?.missed.length) {
    const applied =
      report.honored.length + report.missed.length + report.unverifiable.length;
    trackTmQaFailed({
      story_id: ctx.storyId,
      showcase_id: result.showcaseId,
      source_lang: ctx.sourceLang,
      target_lang: ctx.targetLang,
      missed_count: report.missed.length,
      // Counts only — the missed terms themselves are customer content.
      error_message: `${report.missed.length} of ${applied} glossary terms not honored after retry`,
      action,
    });
  }
}
